import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/** `/settings/csv-column-mappings` (v1.0 Phase 3 Sprint 10、Lite版 CSV列名→ID マッピング管理)。
 *  設計: docs/design/v10-multi-tenant.md Chapter 4.2「csv_column_mappings」。
 *  LiteCsvImportPage.tsx でマッピング未定義エラーが出た場合、この画面へ誘導される。 */

type SourceType = 'monthly_pl' | 'monthly_supplier_costs';
type Mapping = {
  id: string;
  csv_source_type: SourceType;
  csv_column_name: string;
  target_type: 'account_category' | 'supplier';
  target_id: string;
  target_name: string;
  priority: number;
};
type Category = { id: string; name: string };
type Supplier = { id: string; name: string };

const SOURCE_TYPE_LABEL: Record<SourceType, string> = {
  monthly_pl: '月次PL (勘定科目)',
  monthly_supplier_costs: '月次仕入先別コスト',
};

export function CsvColumnMappingsSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const [sourceType, setSourceType] = useState<SourceType>('monthly_pl');
  const targetType = sourceType === 'monthly_pl' ? 'account_category' : 'supplier';

  const { data: mappings = [], isLoading } = useQuery<Mapping[]>({
    queryKey: ['csv-column-mappings', sourceType],
    queryFn: () => apiFetch<Mapping[]>(`/csv-column-mappings?csv_source_type=${sourceType}`),
  });
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['account-categories'],
    queryFn: () => apiFetch<Category[]>('/account-categories'),
    enabled: sourceType === 'monthly_pl',
  });
  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['suppliers-lite'],
    queryFn: () => apiFetch<Supplier[]>('/suppliers-lite'),
    enabled: sourceType === 'monthly_supplier_costs',
  });
  const targetOptions = sourceType === 'monthly_pl' ? categories : suppliers;

  const [creating, setCreating] = useState<{ csv_column_name: string; target_id: string; priority: number } | null>(null);
  const [deleting, setDeleting] = useState<Mapping | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['csv-column-mappings', sourceType] });

  const createMut = useMutation({
    mutationFn: (body: { csv_source_type: SourceType; csv_column_name: string; target_type: 'account_category' | 'supplier'; target_id: string; priority: number }) =>
      apiFetch('/csv-column-mappings', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { invalidate(); setCreating(null); toast.success('マッピングを追加しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/csv-column-mappings/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); setDeleting(null); toast.success('マッピングを削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">{t('entity.csv_column_mappings', 'CSV列名マッピング')}</h2>
          <p className="text-sm text-neutral-sub mt-1">
            CSV上の列名 (縦持ちなら勘定科目/仕入先のセル値、横持ちなら列見出し) を、マスタのIDに対応付けます。
            未定義の列があると Lite版取込 でエラーになります。
          </p>
        </div>
        <button type="button" onClick={() => setCreating({ csv_column_name: '', target_id: '', priority: 0 })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap">
          <Plus size={16} /> マッピング追加
        </button>
      </div>

      <div className="flex gap-2">
        {(Object.keys(SOURCE_TYPE_LABEL) as SourceType[]).map((t) => (
          <button key={t} type="button" onClick={() => setSourceType(t)}
            className={`px-3 py-1.5 text-sm rounded-full whitespace-nowrap transition ${sourceType === t ? 'bg-brand-red text-white' : 'bg-surface-sunken text-neutral-sub hover:text-neutral-text'}`}>
            {SOURCE_TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-4 py-2">{t('field.csv_column_mappings.csv_column_name', 'CSV列名')}</th>
                <th className="text-left px-4 py-2">マッピング先</th>
                <th className="text-left px-4 py-2 w-20">{t('field.csv_column_mappings.priority', '優先度')}</th>
                <th className="w-16 px-4 py-2 relative"><span className="sr-only">操作</span></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id} className="border-t border-neutral-line">
                  <td className="px-4 py-2 font-mono text-xs">{m.csv_column_name}</td>
                  <td className="px-4 py-2">{m.target_name}</td>
                  <td className="px-4 py-2 text-neutral-sub">{m.priority}</td>
                  <td className="px-4 py-2 text-right">
                    <button type="button" aria-label="削除" onClick={() => setDeleting(m)}
                      className="p-1 text-neutral-sub hover:text-danger-text"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {mappings.length === 0 && <EmptyState message="マッピングが登録されていません" description="「マッピング追加」から登録してください" />}
        </div>
      )}

      <Modal
        open={creating !== null}
        title="マッピング追加"
        onClose={() => setCreating(null)}
        size="sm"
        footer={creating && (
          <>
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="mapping-create-form" disabled={createMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending ? '作成中…' : '作成'}
            </button>
          </>
        )}
      >
        {creating && (
          <form id="mapping-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              const csvColumnName = creating.csv_column_name.trim();
              if (!csvColumnName) { toast.error('CSV列名を入力してください'); return; }
              if (!creating.target_id) { toast.error('マッピング先を選択してください'); return; }
              createMut.mutate({
                csv_source_type: sourceType, csv_column_name: csvColumnName,
                target_type: targetType, target_id: creating.target_id, priority: creating.priority,
              });
            }}
            className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.csv_column_mappings.csv_column_name', 'CSV列名')}*</label>
              <input required autoFocus value={creating.csv_column_name} onChange={(e) => setCreating({ ...creating, csv_column_name: e.target.value })}
                placeholder="例：家賃、事務所家賃" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">マッピング先 ({sourceType === 'monthly_pl' ? '勘定科目' : '仕入先'})*</label>
              <select required value={creating.target_id} onChange={(e) => setCreating({ ...creating, target_id: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                <option value="">選択してください</option>
                {targetOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.csv_column_mappings.priority', '優先度')} (同一列名に複数マッピングがある場合、数値が大きいものを優先)</label>
              <input type="number" value={creating.priority} onChange={(e) => setCreating({ ...creating, priority: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </form>
        )}
      </Modal>

      {deleting && (
        <Modal open title="マッピングを削除しますか？" onClose={() => setDeleting(null)} size="sm"
          footer={(
            <>
              <button type="button" onClick={() => setDeleting(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="button" onClick={() => deleteMut.mutate(deleting.id)} disabled={deleteMut.isPending}
                className="px-4 py-2 rounded bg-danger-text text-white hover:opacity-90 disabled:opacity-50">
                {deleteMut.isPending ? '削除中…' : '削除する'}
              </button>
            </>
          )}>
          <p className="text-sm">「{deleting.csv_column_name}」→「{deleting.target_name}」のマッピングを削除します。今後のCSV取込でこの列名は再度エラーになります。</p>
        </Modal>
      )}
    </div>
  );
}
